import { useRef, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useId } from '../../hooks/useId'
import styles from './TextArea.module.css'

export function TextArea({
  label,
  labelIcon,
  optional = false,
  assistiveText,
  error = false,
  errorMessage,
  maxLength,
  dynamic = false,
  disabled = false,
  readOnly = false,
  id,
  className = '',
  value,
  onInput,
  ...textareaProps
}) {
  const generatedId = useId()
  const fieldId = id || generatedId
  const textareaRef = useRef(null)

  const resize = () => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  useLayoutEffect(() => {
    resize()
  }, [value, dynamic])

  const handleInput = (e) => {
    resize()
    onInput?.(e)
  }

  const containerClasses = [
    styles.textarea__container,
    error ? styles['input__container--error'] : '',
    disabled ? styles['input__container--disabled'] : '',
    readOnly ? styles['input__container--readonly'] : '',
  ]
    .filter(Boolean)
    .join(' ')

  const fieldClasses = [styles.textarea__field, dynamic ? styles['textarea__field--dynamic'] : '']
    .filter(Boolean)
    .join(' ')

  const assistiveContent = error ? (
    <span className={[styles.input__assistive, styles['input__assistive--error']].join(' ')}>
      <span className="material-icons-outlined">error_outline</span>
      {errorMessage}
    </span>
  ) : assistiveText ? (
    <span className={styles.input__assistive}>{assistiveText}</span>
  ) : null

  const currentLength = typeof value === 'string' ? value.length : 0

  return (
    <div
      className={[styles.input, readOnly ? styles['input--readonly'] : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      {label && (
        <label htmlFor={fieldId} className={styles.input__label}>
          <span
            className={[styles['input__label-text'], styles['input__label-text--lg']].join(' ')}
          >
            {label}
          </span>
          {labelIcon && <span className={styles['input__label-icon']}>{labelIcon}</span>}
          {optional && <span className={styles['input__label-optional']}>(opcional)</span>}
        </label>
      )}
      <div className={containerClasses}>
        <textarea
          ref={textareaRef}
          id={fieldId}
          className={fieldClasses}
          disabled={disabled}
          readOnly={readOnly}
          value={value}
          maxLength={maxLength}
          onInput={handleInput}
          {...textareaProps}
        />
      </div>
      {!readOnly && (assistiveContent || maxLength) && (
        <div className={styles['input__assistive-row']}>
          {assistiveContent}
          {maxLength && (
            <span className={styles.input__count}>
              {currentLength}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

TextArea.propTypes = {
  label: PropTypes.node,
  labelIcon: PropTypes.node,
  optional: PropTypes.bool,
  assistiveText: PropTypes.node,
  error: PropTypes.bool,
  errorMessage: PropTypes.node,
  maxLength: PropTypes.number,
  dynamic: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string,
  onInput: PropTypes.func,
}
